import PropTypes from 'prop-types';
import { useEffect } from 'react';
import * as Yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// @mui
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import MenuItem from '@mui/material/MenuItem';
import LoadingButton from '@mui/lab/LoadingButton';
// components
import FormProvider, { RHFTextField, RHFSelect } from 'src/components/hook-form';
import { useSnackbar } from 'src/components/snackbar';
// utils
import axios, { API_ENDPOINTS } from 'src/utils/axios';

// ----------------------------------------------------------------------

const ROLE_OPTIONS = [
  { value: 'User', label: 'Utilizador' },
  { value: 'ADMINISTRATOR', label: 'Administrador' },
];

export default function UserFormDialog({ open, onClose, user, onSuccess }) {
  const { enqueueSnackbar } = useSnackbar();
  const isEdit = !!user;

  const UserSchema = Yup.object().shape({
    firstName: Yup.string().required('Nome é obrigatório'),
    lastName: Yup.string().required('Apelido é obrigatório'),
    email: Yup.string().email('Email inválido').required('Email é obrigatório'),
    password: isEdit
      ? Yup.string()
      : Yup.string().min(6, 'Password deve ter pelo menos 6 caracteres').required('Password é obrigatória'),
    role: Yup.string().required('Role é obrigatório'),
  });

  const defaultValues = {
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    role: 'User',
  };

  const methods = useForm({
    resolver: yupResolver(UserSchema),
    defaultValues,
  });

  const {
    reset,
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  useEffect(() => {
    if (user) {
      reset({
        firstName: user.firstname || '',
        lastName: user.lastname || '',
        email: user.email || '',
        password: '',
        role: user.role || 'User',
      });
    } else {
      reset(defaultValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, reset]);

  const onSubmit = handleSubmit(async (data) => {
    try {
      if (isEdit) {
        await axios.put(`${API_ENDPOINTS.users.update}${user.id}`, {
          firstName: data.firstName,
          lastName: data.lastName,
          email: data.email,
          role: data.role,
        });
        enqueueSnackbar('Utilizador atualizado com sucesso', { variant: 'success' });
      } else {
        await axios.post(API_ENDPOINTS.users.create, {
          firstName: data.firstName,
          lastName: data.lastName,
          email: data.email,
          password: data.password,
          role: data.role,
        });
        enqueueSnackbar('Utilizador criado com sucesso', { variant: 'success' });
      }
      onSuccess();
    } catch (error) {
      console.error(error);
      enqueueSnackbar(error.message || 'Ocorreu um erro', { variant: 'error' });
    }
  });

  const handleClose = () => {
    reset(defaultValues);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <FormProvider methods={methods} onSubmit={onSubmit}>
        <DialogTitle>{isEdit ? 'Editar Utilizador' : 'Novo Utilizador'}</DialogTitle>

        <DialogContent>
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <RHFTextField name="firstName" label="Nome" />
              <RHFTextField name="lastName" label="Apelido" />
            </Stack>

            <RHFTextField name="email" label="Email" disabled={user?.isProtected} />

            {!isEdit && (
              <RHFTextField name="password" label="Password" type="password" />
            )}

            <RHFSelect name="role" label="Role">
              {ROLE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </RHFSelect>
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} color="inherit">
            Cancelar
          </Button>
          <LoadingButton type="submit" variant="contained" loading={isSubmitting}>
            {isEdit ? 'Guardar' : 'Criar'}
          </LoadingButton>
        </DialogActions>
      </FormProvider>
    </Dialog>
  );
}

UserFormDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  user: PropTypes.object,
  onSuccess: PropTypes.func,
};
